import { Link } from 'react-router-dom'
import { useInvoiceReady, useMarkInvoiced } from '../api/queries'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDateTime, formatRelative } from '../lib/format'

/**
 * "Til fakturering" (beslutning 147): sagerne, der har nået 100 % og står som "Klar til
 * faktura". Kun kontoret. Den, der har ventet længst, står øverst. "Faktureret" sætter
 * sagen til "Færdig", så den forsvinder herfra - selve fakturaen laves i Ordrestyring.
 */
export function InvoicingPage() {
  const { isIntranetAdmin } = useAuth()
  const { data, isLoading, error } = useInvoiceReady(isIntranetAdmin)
  const mark = useMarkInvoiced()

  if (!isIntranetAdmin) return <EmptyState>Siden er kun for kontoret.</EmptyState>
  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const projects = data ?? []

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title={`💰 Klar til faktura (${projects.length})`}>
        <p className="small muted" style={{ marginTop: 0 }}>
          Sager, hvor alle opgaver er færdige (100 %). Tryk "Faktureret", når fakturaen er sendt - så står
          sagen som "Færdig" og forsvinder fra listen.
        </p>

        {mark.error && <ErrorBox error={mark.error} />}

        {projects.length === 0 ? (
          <EmptyState>Ingen sager venter på at blive faktureret.</EmptyState>
        ) : (
          <div className="stack" style={{ gap: 0 }}>
            {projects.map((p) => {
              const town = [p.postalCode, p.city].filter(Boolean).join(' ')
              const address = [p.address, town].filter(Boolean).join(', ')
              return (
                <div key={p.id} className="invoice-row">
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 800 }}>
                      {p.orderNumber ? `Ordre ${p.orderNumber}` : 'Uden ordrenummer'}
                      {p.kind === 'service' && <span className="pill" style={{ marginLeft: 8 }}>Service</span>}
                    </div>
                    <Link to={`/projects/${p.id}`} style={{ fontWeight: 650 }}>
                      {p.name}
                    </Link>
                    <div className="small">{p.customer ?? 'Ingen kunde'}</div>
                    {address && <div className="small muted">{address}</div>}
                    <div className="tiny muted" title={formatDateTime(p.readySince)}>
                      Klar {formatRelative(p.readySince)} · {p.taskCount} {p.taskCount === 1 ? 'opgave' : 'opgaver'}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                    {p.projectManager && (
                      <span className="row tiny muted" style={{ gap: 6 }}>
                        <Avatar user={p.projectManager} size={24} />
                        {p.projectManager.displayName}
                      </span>
                    )}
                    <button
                      className="btn btn--primary btn--sm"
                      disabled={mark.isPending}
                      onClick={() => {
                        if (confirm(`Er "${p.name}" faktureret? Sagen sættes til "Færdig".`)) mark.mutate(p.id)
                      }}
                    >
                      ✓ Faktureret
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
